import { getClosestReactModal, getClosestScrollableAncestor } from "utilities/helpers";

export interface IScrollPos {
    posX: number;
    posY: number;
}

export interface IScrollToElementOptions {
    /**
     * When true, and the element is not inside a React modal, scroll the nearest scrollable
     * ancestor (e.g. the BDS HCF content area `.bds-layout-hcf-wrapper--content` that scrolls
     * instead of the window) rather than the window. Defaults to false so callers keep the
     * original window/modal-only behavior. Opt in only where the target lives inside an inner
     * scroll container (e.g. the JPS header rows).
     */
    useScrollableAncestor?: boolean;
}

/**
 * Scrolls to a specific element on the page
 */
export const scrollToElement = function (
    element: HTMLElement | null,
    center?: boolean,
    options?: IScrollToElementOptions,
) {
    if (element) {
        const closestReactModal = getClosestReactModal(element, "body");
        // Modal wins first (some modals strip the window scrollbar). Only when a caller opts in do
        // we look for a real inner scroll container before falling back to scrolling the window.
        const scrollableAncestor =
            options?.useScrollableAncestor && !closestReactModal
                ? getClosestScrollableAncestor(element.parentElement)
                : undefined;
        const elementBoundingRect = element.getBoundingClientRect();

        let context: HTMLElement | (Window & typeof globalThis);
        let heightToScroll: number;
        let viewHeight: number;
        if (closestReactModal) {
            context = closestReactModal;
            heightToScroll =
                elementBoundingRect.top - context.children[0].getBoundingClientRect().top;
            viewHeight = context.offsetHeight;
        } else if (scrollableAncestor) {
            context = scrollableAncestor;
            const containerRect = context.getBoundingClientRect();
            heightToScroll = context.scrollTop + (elementBoundingRect.top - containerRect.top);
            viewHeight = context.clientHeight;
        } else {
            context = window;
            heightToScroll = elementBoundingRect.top + context.pageYOffset;
            viewHeight = context.innerHeight;
        }

        if (center && viewHeight > elementBoundingRect.height) {
            heightToScroll = heightToScroll - (viewHeight - elementBoundingRect.height) / 2;
        }

        scrollWithinRef(context, heightToScroll, true);
    }
};

export const scrollWithinRef = function (
    element: HTMLElement | (Window & typeof globalThis),
    amount: number,
    smooth?: boolean,
) {
    const supportsNativeSmoothScroll = "scrollBehavior" in document.documentElement.style;
    if (supportsNativeSmoothScroll) {
        element.scrollTo({ top: amount, behavior: smooth ? "smooth" : undefined });
    } else {
        element.scrollTo(0, amount);
    }
};

/**
 * Scrolls to a specific element on the page using an ID
 */
export const scrollToElementById = function (
    id: string,
    center?: boolean,
    options?: IScrollToElementOptions,
) {
    const element = document.getElementById(id);
    scrollToElement(element, center, options);
};

export class ScrollPos {
    PosX: number;
    PosY: number;
}

export const saveScrollPosition = () => {
    // eslint-disable-next-line no-restricted-syntax
    const win = window as any;
    const pos = new ScrollPos();
    pos.PosX = win.scrollX;
    pos.PosY = win.scrollY;
    return pos;
};

export const scrollToPosition = (pos: ScrollPos | IScrollPos) => {
    // eslint-disable-next-line no-restricted-syntax
    const win = window as any;
    const x = "PosX" in pos ? pos.PosX : pos.posX;
    const y = "PosY" in pos ? pos.PosY : pos.posY;
    win.scrollTo(x, y);
};
